import { enInboxCalendarInvite, enInboxCalendarResponse } from "./enInboxCalendar";
import { enInboxThread } from "./enInboxThread";
import {
	enInboxEditor,
	enInboxIdentity,
	enInboxPendingInvitation,
	enInboxSignature,
	enInboxUndoSend,
	enInboxVariable,
} from "./enInboxComposer";
import { enInboxTour } from "./enInboxTour";

import { enInboxColumns } from "./enInboxColumns";
import { enInboxScoreFilters } from "./enInboxScoreFilters";
import { enInboxTracker } from "./enInboxTracker";
import { enListRecovery } from "./enListRecovery";

export const enInbox = {
	outreach: "Outreach",
	sentimentSaveFailed: "Could not save the sentiment",
	searchPlaceholder: "Search for name or company name ...",
	replyTabs: {
		my: "My replies",
		team: "Team replies",
		all: "All replies",
	},
	bulkDeepResearchSuccess: "Deep Research scheduled: {{queued}} queued, {{skipped}} skipped",
	bulkDeepResearchInsufficientCredits: "Not enough credits to run Deep Research",
	bulkDeepResearchNoPrompt: "No Deep Research prompt configured",
	bulkDeepResearchError: "Failed to start Deep Research",
	fromAnotherList: "From another list",
	calendarResponse: enInboxCalendarResponse,
	calendarInvite: enInboxCalendarInvite,
	actionBar: {
		add: "Add",
	},
	cc: {
		label: "Cc:",
		replyNotSentToCc: "When you answer now, the email will not be sent to the Cc.",
	},
	thread: enInboxThread,
	leadLabels: {
		stateLegend: "State legend",
		types: {
			NEW_LEAD: "New lead",
			IN_ACTIVE_CAMPAIGN: "In active campaign",
			CONNECTED_ON_LINKEDIN: "Connected on LinkedIn",
			PREVIOUS_OUTREACH: "Previous outreach",
			ON_OTHER_LISTS: "On other lists",
		},
		previousOutreachFrom: "Previous outreach from {{lists}}",
		connectedOnLinkedinWithCount: "Connected on LinkedIn ({{count}})",
		onOtherListsHeader_one: "On {{count}} other list:",
		onOtherListsHeader_other: "On {{count}} other lists:",
	},
	relationshipHover: {
		reviewChat: "Review chat",
		loading: "Loading…",
		error: "Couldn't load relationship details.",
		empty: "No relationship details yet.",
		activeCampaign: "In active campaign",
		pastOutreach: "Past outreach",
		connectedOnLinkedin: "Connected on LinkedIn",
		onOtherLists: "On other lists",
		assignedOn: "Assigned {{date}}",
		connectedOn: "Connected {{date}}",
		ended: "Ended",
		campaignCount_one: "{{count}} campaign",
		campaignCount_other: "{{count}} campaigns",
		peopleCount_one: "{{count}} person",
		peopleCount_other: "{{count}} people",
		listCount_one: "{{count}} list",
		listCount_other: "{{count}} lists",
		sentiment: {
			INTERESTED: "Interested",
			NOT_INTERESTED: "Not Interested",
			BAD_TIMING: "Bad Timing",
			WRONG_PERSON: "Wrong Person",
			NEUTRAL: "Neutral",
		},
	},
	draft: {
		relativeTiming_one: "{{count}} day later",
		relativeTiming_other: "{{count}} days later",
		acceptedConnectionTooltip: "This is based on the date of the accepted connection request",
		whyThis: "Why this?",
		reasoningHeading: "Why this draft?",
	},
	signature: enInboxSignature,
	variable: enInboxVariable,
	sendTest: {
		triggerButton: "Send test",
	},
	/**
	 * Shown when the API refuses a send with `PLAN_INACTIVE` (PREC-3900): the company cancelled,
	 * so the inbox stays readable but nothing may go out. Without it a cancelled customer clicked
	 * Send and got total silence — no toast, no error, an unhandled rejection in the console.
	 */
	planInactive:
		"Your subscription has ended — you can still read your inbox, but messages can no longer be sent. Reactivate your subscription to send again.",
	/** Fallback toast for any other rejected send — without it the failure is silent. */
	sendFailed: "The message could not be sent. Please try again.",
	identity: enInboxIdentity,
	pendingInvitation: enInboxPendingInvitation,
	editor: enInboxEditor,
	undoSend: enInboxUndoSend,
	composeMenu: {
		more: "More actions",
		tellAiWhatToWrite: "Tell AI what to write",
	},
	copywritingDraft: {
		save: "Save draft",
		saved: "Draft saved",
		saveError: "Failed to save draft. Please try again.",
	},
	markAsDoneSuccess: "Marked as done",
	markAsDoneError: "Failed to mark as done. Please try again.",
	archiveSuccess: "Lead archived",
	archiveError: "Failed to archive lead. Please try again.",
	tour: enInboxTour,
	signal: "Signal",
	fundingMaRound: "Funding Round",
	fundingMaInvestors: "Investors",
	fundingMaAmount: "Amount",
	fundingMaAnnounced: "Announced",
	fundingMaCompanyInterestedIn: "Company Interested In",
	fundingMaPercentage: "Acquired Percentage",
	fundingMaPostUrl: "Funding Post URL",
	fundingMaAcquiredByName: "Acquired By",
	fundingMaAcquiredByLinkedinUrl: "Acquired By LinkedIn URL",
	followUpType: {
		title: "Follow Up by",
		ALL: "All",
		EXISTING_CONNECTION: "Existing Connection",
		EXISTING_THREAD: "Existing Thread",
	},
	companyCategoryType: "Company Category Type",
	assignStatus: {
		DRAFT: "New",
		ASSIGNED: "Scheduled",
		ASSIGNING: "Assigning to campaign",
		RUNNING: "Running",
		SAVED_FOR_LATER: "Review later",
		BLACKLIST: "Blacklist",
		NOT_NOW: "Contact later",
		NO_REPLY: "No Reply in campaign",
		REPLIES: "Replied in campaign",
		TO_BE_NURTURED: "To be nurtured in campaign",
		DO_NOT_CONTACT: "Do not contact in campaign",
		ENDED: "Sequence ended",
		PAUSED: "Paused in campaign",
		ARCHIVED: "Ignored",
		ALL: "All",
		MESSAGE_SENT: "Manually sent",
		DONE: "Done",
		UNDER_REVIEW: "Under Review",
		FOLLOW_UP: "Follow Up",
		TRACKED_PROFILE: "Tracked Profile",
	},
	generalCountry: "Location",
	reportedEmployees: "Reported Employees",
	overallScore: "Lead Score",
	companyCategory: "Company Segment",
	personaCategory: "Person Category",
	exclusionReason: "Exclusion Reason",
	blacklistWarning: {
		unsubscribed: "This lead unsubscribed — outreach is disabled",
		blacklisted: "This lead is blacklisted — contacting them asks you to confirm first",
	},
	chatDisabled: {
		unsubscribed: "Chat is disabled — this lead unsubscribed and must not be contacted.",
		blacklisted:
			"This lead is blacklisted. Contacting them asks you to confirm first, and takes them off the blacklist.",
	},
	previousEmployers: "Previous Employers",
	previousJobTitle: "Previous Job Title",
	select: "Select",
	assignedTo: "In campaign",
	linkedinUrl: "LinkedIn URL",
	email: "E-Mail",
	seniority: "Seniority",
	listPanel: "List",
	filter: "Filter",
	estimatedNumEmployees: "Number of Employees",
	from_estimatedNumEmployees: "From",
	till_estimatedNumEmployees: "To",
	genderProbability: "Gender Confidence",
	from_genderProbability: "From",
	till_genderProbability: "To",
	companyLinkedinUrl: "Company LinkedIn URL",
	companyPeopleCheck: "Company People Check",
	holdingCompany: "Holding Company",
	persons: "Persons",
	addCompany: "Add Company",
	headline: "Inbox",
	subtitle: "Here you can find all your potential prospects",
	list: "Already on other lists",
	status: "Status",
	name: "Name",
	title: "Job Title",
	level: "Level",
	campaign: "Campaign",
	companyName: "Company Name",
	company: "Company",
	companyEmail: "Company Email",
	connections: "Connected on LinkedIn",
	industry: "Industry",
	normalizedCompanyName: "Normalized Company Name",
	companyStats: "Company Stats",
	companyFounded: "Company Founded",
	companyEmployees: "Number of Employees",
	personLocation: "Person Location",
	companyLocation: "Company Location",
	anyCompanyLocation: "Any Company Location",
	language: "Language",
	gender: "Gender",
	genderConfirmOption: "Confirm",
	genderConfirmedTooltip: "Gender is confirmed.",
	genderUnsureTooltip:
		"Gender confidence is below 90%. Please change or confirm this gender to avoid wrong salutations.",
	genderSetTooltip: "Set gender",
	genderRightClickTooltip: "Right-click to change the gender",
	genderCancelTooltip: "Cancel without saving",
	founded: "Founded",
	jobs: "Jobs",
	previousEmployer: "Previous Employer",
	all: "All",
	person_locations: "Person Location",
	currentPage: "Current Page",
	maxPerCompany: "MaxPerCompany",
	contactInfo: "ContactInfo",
	tabs: {
		unassigned: "New",
		assigned: "Assigned",
		blacklist: "Blacklist",
		savedForLater: "To be reviewed",
		archived: "Ignored",
		done: "Done",
		underReview: "Under Review",
		trackedProfile: "Tracked Profile",
		companies: "Companies",
	},
	otherLists: "Other Lists",
	addedToCampaignAt: "Added to Campaign At",
	assignedStatus: "Status",
	inboxOutbox: "Inbox / Outbox",
	locationInbox: "Inbox",
	locationOutbox: "Outbox",
	openChat: "Open chat",
	lastNodeType: "Last Step",
	lastScheduledAt: "Last Scheduled At",
	personInCrm: "Person is in CRM",
	companyInCrm: "Company is in CRM",
	deepResearchResponse: "Deep Research",
	fitAssessment: "Fit Assessment",
	lastContactAt: "Last Contact",
	lastResponseAt: "Last Response",
	lastAssignStatus: "Last Assign Status",
	hasAnyReply: "Has Replied",
	lastSentiment: "Last Sentiment",
	firstSentiment: "First Sentiment",
	bestSentiment: "Best Sentiment",
	crmProspect: "Person in CRM",
	crmOrganization: "Company in CRM",
	primaryDomain: "Primary Domain",
	fitLevel: {
		STRONG: "Strong Fit",
		MEDIUM: "Medium Fit",
		WEAK: "Weak Fit",
		NO_FIT: "No Fit",
	},
	lastMessage: "Last Message",
	nodeType: {
		emailNode: "Send email",
		linkedinNode: "Send LinkedIn Message",
		linkedinInviteNode: "Send LinkedIn Invite",
		linkedinVisitNode: "Trigger LinkedIn Visit",
		linkedinInmailNode: "Send LinkedIn InMail",
		linkedinProfileVisit: "Trigger LinkedIn Profile Visit",
	},
	otherEvents: "Events",
	event: "Event",
	eventAttendees: "Attendees",
	eventAt: "Event at",
	eventDescription: "Event description",
	assignTo: "Campaign",
	googleMaps: "Google Maps",
	reviewCount: "Amount of ratings",
	rating: "Rating",
	type: "Type",
	businessType: "Business type",
	socialMedia: "Social Media",
	website: "Website",
	sourceData: "Source Data",
	phoneNumber: "Phone Number",
	companyCountry: "Company HQ Country",
	companyCity: "Company City",
	companyState: "Company State",
	city: "Personal City",
	state: "Personal State",
	country: "Personal Country",
	selectColumns: "Select columns",
	close: "Close",
	companyPhone: "Company Phone",
	jobTitle: "Job Position",
	jobDescription: "Job Description",
	postedAt: "Posted At",
	employmentType: "Employment Type",
	...enInboxColumns,
	delete: {
		title: "Delete Column",
		confirmation: "Are you sure you want to delete this column?",
	},
	POST: "LinkedIn Profile Posts",
	COMMENT: "LinkedIn Profile Comments",
	LIKE_TO_POST: "LinkedIn Profile Likes to Post",
	LIKE_TO_COMMENT: "LinkedIn Profile Likes to Comment",
	COMMENT_TO_POST: "LinkedIn Profile Comments to Post",
	LINKEDIN_PROFILE_POSTS: "Posts",
	LINKEDIN_PROFILE_COMMENTS: "Comments",
	LINKEDIN_PROFILE_LIKES_TO_POST: "Likes to Post",
	LINKEDIN_PROFILE_LIKES_TO_COMMENT: "Likes to Comment",
	LINKEDIN_PROFILE_COMMENTS_TO_POST: "Comments to Post",
	LINKEDIN_PROFILE_COMMENTS_TO_COMMENTS: "Comments to Comments",
	content: "Post",
	contentResponse: "Comment",
	confirmAssignment: "Confirm Assignment",
	searchForEmail: "Search for email",
	bulkLeadScoringSuccess: "Lead scoring triggered: {{scheduled}} scheduled, {{skipped}} skipped.",
	bulkLeadScoringNoConfig:
		"No lead scoring configuration found. Please configure lead scoring first.",
	leadScoringInProgress: "Lead Scoring in progress",
	leadScoringNotActive: "No lead scoring is active for this workspace",
	runAiSnippets: "Run AI Snippets",
	runAiSnippetsConfirm: "Run AI snippets in campaign messages for {{count}} selected prospects?",
	runAiSnippetsConfirmSingle: "Run AI snippets in campaign messages for {{name}}?",
	runAiSnippetsSuccess: "AI snippet jobs queued: {{queued}} queued, {{skipped}} skipped.",
	runAiSnippetsError: "An error occurred while queueing AI snippet jobs.",
	revertAssignment: "Revert to be reviewed",
	unarchiveAssignment: "Remove from ignored",
	confirmRevertAssignment: "Confirm Revert to be reviewed",
	confirmUnarchiveAssignment: "Confirm remove from ignored",
	unarchiveToDraftAssignment: "Move to drafts",
	confirmUnarchiveToDraft: "Confirm move to drafts",
	unarchiveToReviewAssignment: "Move to review",
	confirmUnarchiveToReview: "Confirm move to review",
	archive: "Ignore leads",
	reassign: "Add to campaign",
	campaignAssignment: "Add to campaign",
	listAssignment: "Assign to list",
	personAssignment: "Assign to person",
	person: "Person",
	saveForLater: "To be reviewed",
	excludeFromList: "Exclude from list",
	addToList: "To Found",
	revertToBeChecked: "revert To be reviewed",
	toBlackList: "To Blacklist",
	reportRecord: "Report Record",
	bulkStatusChangeSuccess: "Status changed successfully",
	addToBlackListSuccess: "Added to blacklist successfully",
	reportReasonSent: "Report reason sent successfully",
	bulkAssignSuccess: "Assigned successfully",
	bulkAssignFailed: "Assigning failed",
	bulkAssignRevertSuccess: "Reverted successfully",
	addingToBlacklist: "Adding to blacklist",
	// PREC-4356: an entry can be removed again under Do Not Contact → Blacklist, so say where.
	addingToBlacklistConfirmationMessage:
		"Are you sure you want to add this lead to the Blacklist? They will not be contacted again. You can remove them later under Do Not Contact → Blacklist. Please specify if you want to add only this person to the blacklist or the entire company.",
	addEntireCompanyToBlacklist: "Add entire company to blacklist",
	unsubscribeLeadTitle: "Mark as unsubscribed",
	unsubscribeLeadConfirmationMessage:
		"This lead asked not to be contacted again. They move to the blacklist as unsubscribed and will never be contacted again. This is permanent: an unsubscribe cannot be taken off the blacklist from the product.",
	unsubscribeLead: "Unsubscribe",
	saveForLaterConfirmationMessage: "Add this lead to review later?",
	excludeFromListConfirmationMessage: "Are you sure you want to exclude this lead from the list?",
	excludeFromListNotEligible:
		"Only Found or Not found leads in New or Saved for later can be excluded.",
	excludeFromListSkipped_one:
		"{{count}} lead was not excluded: only Found or Not found leads in New or Saved for later can be excluded.",
	excludeFromListSkipped_other:
		"{{count}} leads were not excluded: only Found or Not found leads in New or Saved for later can be excluded.",
	addToListConfirmationMessage: "Are you sure you want to add this lead to the list?",
	specifyReason: "Please, specify the reason of reporting record.",
	specifyProblem: "Please, specify the problem.",
	revert: "Revert",
	revertFromBlacklist: "Revert from Blacklist",
	checkLater: "Review later",
	addColumn: "Add Column",
	filters: "Filters",
	columns: "Columns",
	sortBy: "Sort by",
	sortByDefault: "Default",
	sortBySearchPlaceholder: "Search…",
	sortByMostUsed: "Most used",
	sortByOptions: {
		lastAddedEnriched: "Last added / enriched",
		leadPriority: "Lead Priority",
		companyName: "Company Name",
		companyCategory: "Company Category",
		personaCategory: "Persona Category",
		jobTitle: "Job Title",
		jobLevel: "Job Level",
		employeeSize: "Employee Size",
		bestReply: "Best reply",
	},
	quickFilters: {
		priorityTitle: "Qualified leads",
		sentimentTitle: "Replies",
	},
	sentimentFilterOptions: {
		ALL: "All replies",
		INTERESTED: "Interested",
		BAD_TIMING: "Bad timing",
		NEUTRAL: "Neutral",
		WRONG_PERSON: "Wrong person",
		NOT_INTERESTED: "Not interested",
	},
	foundedYear: "Founded Year",
	likes: "Likes",
	comments: "Comments",
	shares: "Shares",
	comment: "Comment",
	contentDate: "Content Date",
	followers: "Followers",
	sourceType: "Source Type",
	reply: "Reply",
	message: "Message",
	responseStatus: "Status",
	jobCountry: "Job Country",
	jobCity: "Job City",
	attendees: "Attendees",
	sourceName: "Source Name",
	addedAt: "Added at",
	toBeNurtured: "To be nurtured",
	doNotContact: "Do not contact",
	RUNNING: "Campaign",
	REPLIES: "Replies",
	NO_REPLY: "No Reply",
	DO_NOT_CONTACT: "Do not contact",
	TO_BE_NURTURED: "To be nurtured",
	cityBranch: "Subsidiary City",
	stateBranch: "Subsidiary State",
	countryBranch: "Subsidiary Country",
	...enListRecovery,
	emptyState: {
		never: {
			title: "This list has no leads yet",
			description:
				"Add a source to start finding leads — or add leads manually, from other lists, or from your contacts.",
			addSource: "Add a source",
			goToContacts: "Go to your contacts",
		},
		aged: {
			title: "All clean — add more leads easily",
			description:
				"All leads on this list got either assigned or moved to Ignored. That happens when a lead isn't assigned within 30 days. But don't worry — you can still assign Ignored leads, or add new sources.",
			addSource: "Add a source",
			viewIgnored: "View ignored leads",
		},
		filtered: {
			title: "No relevant leads were found, but the list isn't empty",
			description:
				"None of the leads here are marked Found right now. The rest are waiting in other statuses — this can happen after an import. Want to review them?",
			reviewAll: "Review all {{count}} leads",
			unsure: "Unsure",
			notFound: "Not Found",
			blacklist: "Blacklist",
		},
	},
	...enInboxScoreFilters,
	...enInboxTracker,
	score: "Score",
	branchesInEuropeanUnion: "Branches in European Union",
	branchesInEasternGermany: "Branches in Eastern Germany",
	branchesInSaxony: "Branches in Saxony",
	branchesWorldwide: "Branches Worldwide",
	about: "About",
	createdAt: "Added at",
	salutationDe: "Salutation (DE)",
	formalNameDe: "Formal Name (DE)",
	salutationEn: "Salutation (EN)",
	connectedAt: "Connected at",
	followSince: "Following since",
	pinterest: "Pinterest",
	facebook: "Facebook",
	yelp: "Yelp",
	tiktok: "TikTok",
	instagram: "Instagram",
	snapchat: "Snapchat",
	twitter: "Twitter",
	youtube: "YouTube",
	github: "GitHub",
	roleChangeDate: "Role Change Date",
	roleChangeType: "Role Change Type",
	"Does not work there anymore": "Does not work there anymore",
	bulkUnarchiveSuccess: "Leads restored",
	searchForPhone: "Search for phone number",
	archiveConfirmationMessage: "Do you want to ignore this lead?",
};
